import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import {
  Loader2, Search, RefreshCw, LogOut, Smartphone, Monitor, Tablet,
  Activity, Clock, AlertTriangle, Users,
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${token}` } });
};

const STATUS_META = {
  active:  { label: 'Active',  cls: 'bg-emerald-600 text-white', dot: 'bg-emerald-500' },
  idle:    { label: 'Idle',    cls: 'bg-amber-500 text-white',   dot: 'bg-amber-500' },
  expired: { label: 'Expired', cls: 'bg-slate-500 text-white',   dot: 'bg-slate-400' },
};

const DeviceIcon = ({ d }) => {
  if (d === 'Mobile') return <Smartphone className="w-3.5 h-3.5" />;
  if (d === 'Tablet') return <Tablet className="w-3.5 h-3.5" />;
  return <Monitor className="w-3.5 h-3.5" />;
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
};

const sinceText = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function SeatHofSessionsPanel() {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [config, setConfig] = useState({ heartbeat_grace_seconds: 120, idle_timeout_seconds: 900 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState('');
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (q.trim()) params.set('q', q.trim());
      const r = await api.get(`/seat/hof/sessions?${params.toString()}`);
      setRows(r.data?.sessions || []);
      setConfig({
        heartbeat_grace_seconds: r.data?.heartbeat_grace_seconds ?? 120,
        idle_timeout_seconds: r.data?.idle_timeout_seconds ?? 900,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load sessions');
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [statusFilter, q]);

  // Initial + auto-refresh every 20s so admin sees status changes live
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    timerRef.current = setInterval(() => { load(); }, 20000);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line
  }, [statusFilter, q]);

  const forceLogout = async (s) => {
    if (!s?.hof_id) return;
    if (!window.confirm(
      `Force logout ${s.hof_name || s.hof_its_id || s.hof_id}?\nAnother device will be able to sign in immediately.`,
    )) return;
    setBusyId(s.hof_id);
    try {
      await api.delete(`/seat/hof/sessions/${encodeURIComponent(s.hof_id)}`);
      toast.success('Session ended. The HOF can log in again now.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusyId(''); }
  };

  const counts = rows.reduce(
    (acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; },
    { active: 0, idle: 0, expired: 0 },
  );

  return (
    <div className="space-y-4" data-testid="hof-sessions-panel">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1">
          <h2 className="text-lg font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-emerald-700" /> HOF Live Sessions
          </h2>
          <p className="text-xs text-slate-500">
            One login per HOF. Heartbeat grace <strong>{Math.round(config.heartbeat_grace_seconds / 60)} min</strong>; idle auto-logout <strong>{Math.round(config.idle_timeout_seconds / 60)} min</strong>.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="hof-sessions-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="hof-sessions-stats">
        <Card className={statusFilter === 'all' ? 'border-slate-400 ring-2 ring-slate-200' : 'cursor-pointer hover:shadow'} onClick={() => setStatusFilter('all')}>
          <CardContent className="p-3 text-center">
            <div className="text-xs font-semibold uppercase text-slate-500">All</div>
            <div className="text-2xl font-extrabold">{rows.length}</div>
          </CardContent>
        </Card>
        {['active', 'idle', 'expired'].map(s => (
          <Card
            key={s}
            className={`cursor-pointer transition ${statusFilter === s ? 'ring-2 ring-offset-1 border-slate-400' : 'hover:shadow'}`}
            onClick={() => setStatusFilter(s)}
            data-testid={`hof-sessions-stat-${s}`}
          >
            <CardContent className="p-3 text-center">
              <div className="flex items-center justify-center gap-1 text-xs font-semibold uppercase text-slate-500">
                <span className={`w-2 h-2 rounded-full ${STATUS_META[s].dot}`} /> {STATUS_META[s].label}
              </div>
              <div className="text-2xl font-extrabold">{counts[s] || 0}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by HOF ITS or name"
            className="pl-8"
            data-testid="hof-sessions-search"
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && rows.length === 0 ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-600" /></div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">
              <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-slate-400" />
              No active HOF sessions.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 text-left font-semibold">HOF</th>
                    <th className="px-2 py-2 text-left font-semibold">ITS</th>
                    <th className="px-2 py-2 text-left font-semibold">Device · Browser</th>
                    <th className="px-2 py-2 text-left font-semibold">IP</th>
                    <th className="px-2 py-2 text-left font-semibold">Login</th>
                    <th className="px-2 py-2 text-left font-semibold">Last Active</th>
                    <th className="px-2 py-2 text-left font-semibold">Status</th>
                    <th className="px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map(r => {
                    const meta = STATUS_META[r.status] || STATUS_META.expired;
                    return (
                      <tr key={r.hof_id} data-testid={`hof-session-row-${r.hof_its_id || r.hof_id}`}>
                        <td className="px-2 py-1.5">
                          <div className="font-medium truncate max-w-[220px]" title={r.hof_name || ''}>{r.hof_name || '—'}</div>
                          <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{r.mohalla || ''}{r.mohalla && r.sector ? ' · ' : ''}{r.sector || ''}</div>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-xs">{r.hof_its_id || '—'}</td>
                        <td className="px-2 py-1.5">
                          <span className="inline-flex items-center gap-1 text-xs">
                            <DeviceIcon d={r.device} /> {r.device} · {r.browser}
                          </span>
                        </td>
                        <td className="px-2 py-1.5 font-mono text-xs text-slate-600">{r.ip || '—'}</td>
                        <td className="px-2 py-1.5 text-xs">
                          <Clock className="w-3 h-3 inline mr-0.5 text-slate-400" />
                          {sinceText(r.issued_at)}
                          <div className="text-[10px] text-slate-400">{fmt(r.issued_at)}</div>
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          <Activity className="w-3 h-3 inline mr-0.5 text-slate-400" />
                          {sinceText(r.last_seen)}
                        </td>
                        <td className="px-2 py-1.5">
                          <Badge className={`${meta.cls} text-[10px]`}>{meta.label}</Badge>
                        </td>
                        <td className="px-2 py-1.5 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-red-600"
                            disabled={busyId === r.hof_id || r.status === 'expired'}
                            onClick={() => forceLogout(r)}
                            data-testid={`hof-session-force-logout-${r.hof_its_id || r.hof_id}`}
                          >
                            {busyId === r.hof_id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <><LogOut className="w-3.5 h-3.5 mr-1" /> Force Logout</>
                            )}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
